// ArChat Layout Fix
// Created: 12-03-2026 22:38:36
// Author: SIVOL® - Aldo Frias - Argentina

import { useMemo } from 'react';
import { useLanguage } from '../../context/LanguageContext';
import { useTheme } from '../../context/ThemeContext';
import { substitutePlanPlaceholders } from '../../utils/planNormalizer';
import { FEATURES } from '../../config/featureFlags';
import { shouldBlockI18nStringResult } from '../../utils/i18n/guardI18nKeyLeak';
import { devLog } from '../../utils/devConsoleLog';
import './PlanHelp.css';

/**
 * Línea de ayuda de planes: si no hay datos de API y el texto no usa placeholders, mostrar el texto; si hay placeholders, "No disponible".
 * @param {string} raw
 * @param {object|null} limits
 * @param {(k: string) => string} tLimit
 * @returns {string}
 */
function planLineText(raw, limits, tLimit) {
  if (raw == null || typeof raw !== 'string') return '';
  if (limits == null) {
    return raw.includes('{{') ? tLimit('dashboard.not_available') : raw;
  }
  return substitutePlanPlaceholders(raw, limits, tLimit);
}

/**
 * Función para detectar texto en español
 * Detecta caracteres específicos del español (áéíóúñ) y palabras comunes
 * NOTA: Esta función es conservadora para evitar falsos positivos
 */
const detectSpanishText = (text) => {
  if (!text || typeof text !== 'string') return false;
  
  // Caracteres específicos del español (más confiable)
  const spanishChars = /[áéíóúñÁÉÍÓÚÑ]/;
  if (spanishChars.test(text)) return true;
  
  // Palabras MUY específicas del español que NO aparecen en inglés
  // Solo palabras que son exclusivamente españolas para evitar falsos positivos
  const spanishWords = [
    'puedes', 'puede', 'pueden',
    'gratuito', 'ilimitados', 'ilimitadas', 'ilimitado',
    'contactos', 'conversaciones', 'exportar',
    'precio', 'beneficios', 'actualizar',
    'facturación', 'penalizaciones', 'funcionalidades',
    'límites', 'restricciones', 'disponibles',
    'configura', 'personalizadas', 'avanzadas',
    'estadísticas', 'análisis', 'soporte',
    'prioritario', 'técnico', 'dedicado',
    'hasta', 'día', 'días'
  ];
  
  const lowerText = text.toLowerCase();
  // Solo detectar si la palabra está como palabra completa (no como parte de otra palabra)
  return spanishWords.some(word => {
    const regex = new RegExp(`\\b${word}\\b`, 'i');
    return regex.test(lowerText);
  });
};

const PlanHelp = ({ helpTranslations = {}, limitsFree = null, limitsPro = null }) => {
  const { currentLanguage } = useLanguage();
  const { theme: currentTheme } = useTheme();
  const isDark = currentTheme === 'dark';
  const isArgentina = currentTheme === 'argentina';

  /** t para formatLimit (Ilimitado/Unlimited, No disponible/Not available). */
  const tLimit = useMemo(() => (key) => {
    if (key === 'dashboard.unlimited') return helpTranslations['dashboard.unlimited'] ?? (currentLanguage === 'es' ? 'Ilimitado' : 'Unlimited');
    if (key === 'dashboard.not_available') return helpTranslations['dashboard.not_available'] ?? (currentLanguage === 'es' ? 'No disponible' : 'Not available');
    return helpTranslations[key] ?? key;
  }, [helpTranslations, currentLanguage]);

  /**
   * Función de traducción ESTRICTA - MODO ANTI-ESPAÑOL
   * REGLA: Si idioma !== "es" y no hay traducción → ERROR explícito
   * NUNCA usar español como fallback para otros idiomas
   * 
   * @param {string} key - Clave de traducción
   * @returns {string} - Texto traducido o mensaje de error explícito
   */
  const t = useMemo(() => {
    return (key) => {
      if (helpTranslations && typeof helpTranslations[key] === 'string' && helpTranslations[key].trim() === '') {
        return '';
      }
      // 1. Intentar traducción del idioma actual (OBLIGATORIO)
      if (helpTranslations && helpTranslations[key] && typeof helpTranslations[key] === 'string' && helpTranslations[key].trim() !== '') {
        const translation = helpTranslations[key];
        
        // VALIDACIÓN ANTI-ESPAÑOL: Si el idioma NO es español, verificar que no haya texto en español
        // SOLO verificar caracteres específicos del español (áéíóúñ) - más confiable
        if (currentLanguage !== 'es') {
          const spanishChars = /[áéíóúñÁÉÍÓÚÑ]/;
          if (spanishChars.test(translation)) {
            console.error(`❌ [I18N_ERROR] Texto en español detectado (caracteres especiales) en idioma "${currentLanguage}" para clave "${key}": "${translation.substring(0, 100)}"`);
            // NO retornar el texto en español, mostrar error explícito
            return `[Translation error: Spanish text detected]`;
          }
          // Verificar palabras MUY específicas del español que NO aparecen en inglés
          // Solo palabras que son exclusivamente españolas
          const spanishSpecificWords = ['puedes', 'puede', 'pueden', 'gratuito', 'ilimitados', 'ilimitadas', 'facturación', 'penalizaciones', 'límites', 'hasta', 'día', 'días', 'contactos', 'conversaciones'];
          const lowerText = translation.toLowerCase();
          const hasSpanishWord = spanishSpecificWords.some(word => {
            const regex = new RegExp(`\\b${word}\\b`, 'i');
            return regex.test(lowerText);
          });
          if (hasSpanishWord) {
            console.error(`❌ [I18N_ERROR] Palabra en español detectada en idioma "${currentLanguage}" para clave "${key}": "${translation.substring(0, 100)}"`);
            return `[Translation error: Spanish text detected]`;
          }
        }
        
        if (shouldBlockI18nStringResult(translation, key)) {
          console.warn('[I18N_KEY_VISIBLE_BLOCKED][PlanHelp]', key);
          return '';
        }
        return translation;
      }
      
      // 2. MODO ESTRICTO: Si idioma NO es español → ERROR explícito
      if (currentLanguage !== 'es') {
        console.error(`❌ [I18N_STRICT] Traducción faltante para "${key}" en idioma "${currentLanguage}". NO se permite fallback a español.`);
        return '';
      }
      
      // 3. Solo para español: sin exponer clave cruda en UI
      console.warn(`⚠️ [I18N] Traducción faltante para "${key}" en español`);
      return '';
    };
  }, [helpTranslations, currentLanguage]);

  // Verificar que tenemos traducciones cargadas
  const hasTranslations = useMemo(() => {
    const has = helpTranslations && Object.keys(helpTranslations).length > 0;
    if (!has) {
      console.error(`❌ [PLAN_HELP] No hay traducciones cargadas para idioma "${currentLanguage}"`);
      return false;
    }
    
    // Verificar específicamente las claves de planes
    const requiredPlanKeys = [
      'help.plans.title',
      'help.plans.intro',
      'help.plans.free.title',
      'help.plans.free.description',
      'help.plans.free.limits',
      'help.plans.free.point1',
      'help.plans.free.point2',
      'help.plans.free.point3',
      'help.plans.free.point4',
      'help.plans.free.point5',
      'help.plans.free.point6',
      'help.plans.free.point7',
      'help.plans.free.devices',
      'help.plans.pro.title',
      'help.plans.pro.description',
      'help.plans.pro.price',
      'help.plans.pro.benefits',
      'help.plans.pro.point1',
      'help.plans.pro.point2',
      'help.plans.pro.point3',
      'help.plans.pro.point4',
      'help.plans.pro.point5',
      'help.plans.pro.point6',
      'help.plans.pro.point7',
      'help.plans.pro.point8',
      'help.plans.pro.point9',
      'help.plans.pro.point10',
      'help.plans.pro.devices',
      'help.plans.pro.upgrade',
      'help.plans.enterprise.title',
      'help.plans.enterprise.description',
      'help.plans.enterprise.benefits',
      'help.plans.enterprise.point1',
      'help.plans.enterprise.point2',
      'help.plans.enterprise.point3',
      'help.plans.enterprise.point4',
      'help.plans.enterprise.point5',
      'help.plans.enterprise.point6',
      'help.plans.enterprise.point7',
      'help.plans.enterprise.point8',
      'help.plans.enterprise.point9',
      'help.plans.enterprise.devices',
      'help.plans.foundation.devices'
    ];
    
    const missingKeys = requiredPlanKeys.filter((key) => typeof helpTranslations[key] !== 'string');
    
    if (missingKeys.length > 0) {
      console.error(`❌ [PLAN_HELP] Faltan ${missingKeys.length} traducciones para idioma "${currentLanguage}":`, missingKeys);
      return false;
    }
    
    // Validar que NO haya texto en español si el idioma no es español
    // Solo verificar caracteres especiales del español (más confiable)
    if (currentLanguage !== 'es') {
      const spanishChars = /[áéíóúñÁÉÍÓÚÑ]/;
      const spanishDetected = requiredPlanKeys.some(key => {
        const text = helpTranslations[key];
        return text && typeof text === 'string' && spanishChars.test(text);
      });
      
      if (spanishDetected) {
        console.error(`❌ [I18N_ERROR] Se detectó texto en español (caracteres especiales) en traducciones del idioma "${currentLanguage}"`);
      }
    }
    
    devLog(`✅ [PLAN_HELP] Traducciones validadas: ${requiredPlanKeys.length} claves para idioma "${currentLanguage}"`);
    return true;
  }, [helpTranslations, currentLanguage]);

  if (!hasTranslations) {
    return (
      <section className="plan-help-section" style={{ color: isDark ? '#e0e0e0' : '#333' }}>
        <div style={{ padding: '20px', textAlign: 'center' }}>
          <p style={{ color: '#d32f2f', fontWeight: 'bold' }}>
            {currentLanguage === 'es' 
              ? 'Error: No se pudieron cargar las traducciones de planes.'
              : 'Error: Could not load plan translations.'}
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="plan-help-section" style={{ color: isDark ? '#e0e0e0' : '#1a1a1a' }}>
      <h2 style={{ 
        color: isDark ? '#ffffff' : '#111827',
        fontSize: '1.8rem',
        fontWeight: '700',
        marginBottom: '12px'
      }}>
        {t('help.plans.title')}
      </h2>
      
      <p style={{ 
        color: isDark ? '#d0d0d0' : '#555', 
        marginBottom: '32px',
        fontSize: '1rem',
        lineHeight: '1.6',
        whiteSpace: 'pre-line'
      }}>
        {t('help.plans.intro')}
      </p>
      
      {/* Plan FREE — orden DOM: FREE → FOUNDATION → PRO */}
      <div data-plan="FREE">
      <div className="plan-help-card plan-help-free" style={{ 
        backgroundColor: isDark
          ? '#2a1f0f'
          : (isArgentina ? 'rgba(117, 170, 219, 0.10)' : '#fff3e0'),
        border: `2px solid ${isDark ? '#ff9800' : (isArgentina ? '#75AADB' : '#ff9800')}`, 
        borderRadius: '12px', 
        padding: '24px', 
        marginBottom: '24px',
        color: isDark ? '#e0e0e0' : '#1a1a1a',
        boxShadow: isDark
          ? '0 4px 12px rgba(255, 152, 0, 0.2)'
          : (isArgentina ? '0 2px 10px rgba(117, 170, 219, 0.18)' : '0 2px 8px rgba(255, 152, 0, 0.1)')
      }}>
        <h3 style={{ 
          color: isDark ? '#ffcc80' : (isArgentina ? '#0f1a2f' : '#e65100'), 
          marginTop: 0,
          fontSize: '1.4rem',
          fontWeight: '700',
          marginBottom: '12px'
        }}>
          {t('help.plans.free.title')}
        </h3>
        
        <p style={{ 
          color: isDark ? '#d0d0d0' : '#555',
          marginBottom: '16px',
          fontSize: '1rem',
          lineHeight: '1.6'
        }}>
          <strong>{t('help.plans.free.description')}</strong>
        </p>
        
        <div style={{ 
          marginTop: '16px', 
          padding: '12px 16px', 
          backgroundColor: isDark
            ? 'rgba(255, 152, 0, 0.25)'
            : (isArgentina ? 'rgba(117, 170, 219, 0.15)' : 'rgba(255, 152, 0, 0.15)'), 
          borderRadius: '8px',
          marginBottom: '16px',
          border: isDark
            ? '1px solid rgba(255, 152, 0, 0.3)'
            : (isArgentina ? '1px solid rgba(117, 170, 219, 0.35)' : 'none')
        }}>
          <p style={{ 
            margin: 0, 
            fontSize: '0.95rem', 
            color: isDark ? '#ffcc80' : (isArgentina ? '#0f1a2f' : '#e65100'),
            fontWeight: '700'
          }}>
            {t('help.plans.free.limits')}
          </p>
        </div>
        
        <ul className="plan-help-list" style={{ marginTop: '8px', listStyle: 'none', padding: 0 }}>
          {[1, 2, 3, 4, 5, 6, 7].map((num) => {
            const raw = t(`help.plans.free.point${num}`);
            const text = (num >= 1 && num <= 5)
              ? planLineText(raw, limitsFree, tLimit)
              : raw;
            return (
              <li 
                key={`free-point-${num}`}
                style={{ 
                  color: num === 7 ? (isDark ? '#ff9999' : '#d32f2f') : (isDark ? '#e0e0e0' : '#333'),
                  backgroundColor: num === 7 
                    ? (isDark ? 'rgba(255, 153, 153, 0.1)' : 'rgba(255, 235, 238, 0.5)')
                    : (isDark ? 'rgba(255, 255, 255, 0.05)' : 'transparent'),
                  borderLeftColor: num === 7
                    ? (isDark ? '#ff6666' : '#d32f2f')
                    : (isDark ? '#ff9800' : (isArgentina ? '#75AADB' : '#ff9800')),
                  fontWeight: num === 7 ? '600' : '400',
                  padding: '10px 15px',
                  marginBottom: '8px',
                  borderRadius: '8px',
                  borderLeft: '3px solid',
                  paddingLeft: '30px',
                  position: 'relative',
                  fontSize: '0.95rem',
                  lineHeight: '1.6'
                }}
              >
                <span style={{
                  position: 'absolute',
                  left: '12px',
                  color: num === 7
                    ? (isDark ? '#ff6666' : '#d32f2f')
                    : (isDark ? '#ff9800' : (isArgentina ? '#75AADB' : '#ff9800')),
                  fontWeight: 'bold',
                  fontSize: '1.2rem'
                }}>•</span>
                {' '}
                {text}
              </li>
            );
          })}
        </ul>
        <p style={{ marginTop: '12px', fontSize: '0.95rem', color: isDark ? '#ffcc80' : (isArgentina ? '#0f1a2f' : '#e65100'), fontWeight: '600' }}>
          {planLineText(t('help.plans.free.devices'), limitsFree, tLimit)}
        </p>
      </div>
      </div>

      {/* Plan FOUNDATION (Founder) — ayuda: una sola explicación; sin cupo dinámico (valores en la app) */}
      <div data-plan="FOUNDATION">
      <div className="plan-help-card plan-help-enterprise" style={{
        backgroundColor: isDark
          ? '#1a0f2a'
          : (isArgentina ? 'rgba(157, 127, 204, 0.12)' : '#f3e5f5'),
        border: `2px solid ${isArgentina && !isDark ? '#9d7fcc' : '#9c27b0'}`,
        borderRadius: '12px',
        padding: '24px',
        marginBottom: '24px',
        color: isDark ? '#e0e0e0' : '#1a1a1a',
        boxShadow: isDark
          ? '0 4px 16px rgba(156, 39, 176, 0.4)'
          : (isArgentina ? '0 2px 10px rgba(157, 127, 204, 0.25)' : '0 2px 8px rgba(156, 39, 176, 0.2)')
      }}>
        <h3 style={{
          color: isDark ? '#ce93d8' : (isArgentina ? '#0f1a2f' : '#7b1fa2'),
          marginTop: 0,
          fontSize: '1.4rem',
          fontWeight: '700',
          marginBottom: '12px'
        }}>
          {t('help.plans.enterprise.title')}
        </h3>
        {t('help.plans.enterprise.description').trim() ? (
          <p style={{
            color: isDark ? '#d0d0d0' : '#555',
            marginBottom: '16px',
            fontSize: '1rem',
            lineHeight: '1.6',
            whiteSpace: 'pre-line'
          }}>
            {t('help.plans.enterprise.description')}
          </p>
        ) : null}
        <p style={{
          color: isDark ? '#d0d0d0' : '#555',
          marginBottom: '12px',
          fontSize: '0.95rem',
          lineHeight: '1.6'
        }}>
          {t('help.plans.enterprise.benefits')}
        </p>
        <ul className="plan-help-list" style={{ marginTop: '8px', listStyle: 'none', padding: 0 }}>
          {[1, 2, 3, 4, 5, 6, 7].map((num) => (
            <li
              key={`enterprise-point-${num}`}
              style={{
                color: isDark ? '#e0e0e0' : '#7b1fa2',
                backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                borderLeftColor: isDark ? '#9c27b0' : '#9c27b0',
                padding: '10px 15px',
                marginBottom: '8px',
                borderRadius: '8px',
                borderLeft: '3px solid',
                paddingLeft: '30px',
                position: 'relative',
                fontSize: '0.95rem',
                lineHeight: '1.6'
              }}
            >
              <span style={{
                position: 'absolute',
                left: '12px',
                color: isDark ? '#9c27b0' : '#9c27b0',
                fontWeight: 'bold',
                fontSize: '1.2rem'
              }}>•</span>
              {' '}
              {t(`help.plans.enterprise.point${num}`)}
            </li>
          ))}
        </ul>
        {t('help.plans.enterprise.point8').trim() ? (
          <p style={{
            marginTop: '16px',
            marginBottom: 0,
            color: isDark ? '#d0d0d0' : '#555',
            fontSize: '0.95rem',
            lineHeight: '1.65',
            whiteSpace: 'pre-line',
          }}
          >
            {t('help.plans.enterprise.point8')}
          </p>
        ) : null}
        {planLineText(t('help.plans.foundation.devices'), limitsPro, tLimit).trim() ? (
        <p style={{ marginTop: '12px', fontSize: '0.95rem', color: isDark ? '#ce93d8' : '#7b1fa2', fontWeight: '600' }}>
          {planLineText(t('help.plans.foundation.devices'), limitsPro, tLimit)}
        </p>
        ) : null}
      </div>
      </div>

      {/* Plan PRO */}
      <div data-plan="PRO">
      <div className="plan-help-card plan-help-pro" style={{ 
        backgroundColor: isDark
          ? '#0f2410'
          : (isArgentina ? 'rgba(255, 215, 0, 0.12)' : '#e8f5e9'),
        border: `2px solid ${isArgentina && !isDark ? '#FFD700' : '#4caf50'}`, 
        borderRadius: '12px', 
        padding: '24px', 
        marginBottom: '24px',
        color: isDark ? '#e0e0e0' : '#1a1a1a',
        boxShadow: isDark
          ? '0 4px 16px rgba(76, 175, 80, 0.4)'
          : (isArgentina ? '0 2px 10px rgba(255, 215, 0, 0.25)' : '0 2px 8px rgba(76, 175, 80, 0.2)')
      }}>
        <h3 style={{ 
          color: isDark ? '#69f0ae' : (isArgentina ? '#0f1a2f' : '#2e7d32'), 
          marginTop: 0,
          fontSize: '1.4rem',
          fontWeight: '700',
          marginBottom: '12px'
        }}>
          {t('help.plans.pro.title')}
        </h3>
        
        <p style={{ 
          color: isDark ? '#d0d0d0' : '#2e7d32',
          marginBottom: '16px',
          fontSize: '1rem',
          lineHeight: '1.6'
        }}>
          <strong>{t('help.plans.pro.description')}</strong>
        </p>
        
        {FEATURES.SHOW_PLAN_PRICES_IN_UI &&
        typeof helpTranslations['help.plans.pro.price'] === 'string' &&
        helpTranslations['help.plans.pro.price'].trim() &&
        t('help.plans.pro.price').trim() ? (
          <p style={{
            fontSize: '1.2rem',
            fontWeight: '700',
            color: isDark ? '#69f0ae' : (isArgentina ? '#0f1a2f' : '#2e7d32'),
            marginTop: '12px',
            marginBottom: '16px',
            padding: '10px 16px',
            backgroundColor: isDark
              ? 'rgba(76, 175, 80, 0.3)'
              : (isArgentina ? 'rgba(255, 215, 0, 0.18)' : 'rgba(76, 175, 80, 0.15)'),
            borderRadius: '8px',
            display: 'inline-block',
            border: isDark
              ? '1px solid rgba(76, 175, 80, 0.4)'
              : (isArgentina ? '1px solid rgba(255, 215, 0, 0.35)' : 'none'),
          }}>
            <span>{t('help.plans.pro.price')}</span>
          </p>
        ) : null}
        
        <div style={{ 
          marginTop: '16px', 
          padding: '12px 16px', 
          backgroundColor: isDark
            ? 'rgba(76, 175, 80, 0.25)'
            : (isArgentina ? 'rgba(255, 215, 0, 0.16)' : 'rgba(76, 175, 80, 0.15)'), 
          borderRadius: '8px',
          marginBottom: '16px',
          border: isDark
            ? '1px solid rgba(76, 175, 80, 0.3)'
            : (isArgentina ? '1px solid rgba(255, 215, 0, 0.30)' : 'none')
        }}>
          <p style={{ 
            margin: 0, 
            fontSize: '0.95rem', 
            color: isDark ? '#69f0ae' : '#2e7d32',
            fontWeight: '700'
          }}>
            {t('help.plans.pro.benefits')}
          </p>
        </div>
        
        <ul className="plan-help-list" style={{ marginTop: '8px', listStyle: 'none', padding: 0 }}>
          {[1, 2, 3, 4, 5, 6, 7].map((num) => {
            const raw = t(`help.plans.pro.point${num}`);
            const text = (num >= 1 && num <= 5)
              ? planLineText(raw, limitsPro, tLimit)
              : raw;
            return (
              <li 
                key={`pro-point-${num}`}
                style={{ 
                  color: isDark ? '#e0e0e0' : '#2e7d32',
                  backgroundColor: isDark ? 'rgba(255, 255, 255, 0.05)' : 'transparent',
                  borderLeftColor: isDark ? '#4caf50' : '#4caf50',
                  padding: '10px 15px',
                  marginBottom: '8px',
                  borderRadius: '8px',
                  borderLeft: '3px solid',
                  paddingLeft: '30px',
                  position: 'relative',
                  fontSize: '0.95rem',
                  lineHeight: '1.6'
                }}
              >
                <span style={{
                  position: 'absolute',
                  left: '12px',
                  color: isDark ? '#4caf50' : '#4caf50',
                  fontWeight: 'bold',
                  fontSize: '1.2rem'
                }}>•</span>
                {' '}
                {text}
              </li>
            );
          })}
        </ul>
        {t('help.plans.pro.point8').trim() ? (
          <p style={{
            marginTop: '16px',
            marginBottom: 0,
            color: isDark ? '#d0d0d0' : '#333',
            fontSize: '0.95rem',
            lineHeight: '1.65',
            whiteSpace: 'pre-line',
          }}
          >
            {t('help.plans.pro.point8')}
          </p>
        ) : null}
        {planLineText(t('help.plans.pro.devices'), limitsPro, tLimit).trim() ? (
        <p style={{ marginTop: '12px', fontSize: '0.95rem', color: isDark ? '#69f0ae' : '#2e7d32', fontWeight: '600' }}>
          {planLineText(t('help.plans.pro.devices'), limitsPro, tLimit)}
        </p>
        ) : null}
        
        <p style={{ 
          marginTop: '20px', 
          padding: '14px 16px', 
          backgroundColor: isDark ? 'rgba(76, 175, 80, 0.35)' : '#c8e6c9', 
          borderRadius: '8px',
          color: isDark ? '#e0e0e0' : '#2e7d32',
          border: isDark ? '1px solid rgba(76, 175, 80, 0.4)' : 'none',
          fontSize: '0.95rem',
          lineHeight: '1.6'
        }}>
          <strong>{t('help.plans.pro.upgrade')}</strong>
        </p>
      </div>
      </div>
    </section>
  );
};

export default PlanHelp;
